'use client';

import { isAdminV2ToolFieldActive } from '@/admin-v2/tools/tool-field-visibility';
import { UPSCALER_CUSTOM_SIZE_FIELD } from '@/ai/image/tool-contracts/upscaler/output-size';
import { renderAppIcon } from '@/components/icons/app-icon-registry';
import { PremiumCrownIcon } from '@/components/paid-access/premium-crown-icon';
import { AccessControlledSwitch } from '@/components/ui/forms/meta/access-controlled-switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from '@/components/ui/forms/meta/select';
import { cn } from '@/lib/ui/utils';
import { type CSSProperties, useEffect, useRef, useState } from 'react';
import type {
  WorkspaceToolAccessPolicyRule,
  WorkspaceToolParamScalarValue,
  WorkspaceToolParamView,
  WorkspaceToolTemplatePickerView,
} from '../contracts/tool-view';
import type {
  WorkspaceAssetTarget,
  WorkspaceStudioViewActions,
  WorkspaceStudioViewModel,
} from '../contracts/view-model';
import { buildDefaultWorkspaceToolParams } from '../tooling';
import {
  WORKSPACE_CONTINUOUS_SLIDER_THUMB_SIZE_PX,
  getWorkspaceContinuousSliderThumbCenterPx,
  getWorkspaceContinuousSliderThumbInset,
} from './workspace-continuous-slider-geometry';
import { WorkspaceHelpPopover } from './workspace-info-popover';
import { WorkspaceInfoTooltip } from './workspace-info-tooltip';
import { useWorkspaceMobileDrawerOverlayHost } from './workspace-mobile-drawer';
import { groupWorkspaceParameters } from './workspace-param-grouping';
import { presentWorkspaceParameter } from './workspace-param-presentation';
import { WorkspaceParameterCalculator } from './workspace-parameter-calculator';
import {
  WorkspaceModeTabs,
  WorkspaceTemplatePicker,
} from './workspace-template-picker';
import { WorkspaceUpscaleSize } from './workspace-upscale-size';
import { useWorkspaceIsMobileViewport } from './workspace-viewport';

function readNumberInputValue(value: unknown) {
  const numericValue = Number(value);
  return Number.isFinite(numericValue) ? numericValue : '';
}

function isRenderableParamType(
  param: WorkspaceStudioViewModel['activeTool']['fields'][number]
) {
  return (
    param.type === 'text' ||
    param.type === 'url' ||
    param.type === 'select' ||
    param.type === 'multi-select' ||
    param.type === 'array' ||
    param.type === 'switch' ||
    param.type === 'slider' ||
    param.type === 'segmented-slider' ||
    param.type === 'number'
  );
}

function isWorkspaceParamScalar(
  value: unknown
): value is WorkspaceToolParamScalarValue {
  return (
    typeof value === 'string' ||
    typeof value === 'number' ||
    typeof value === 'boolean'
  );
}

function getOptionValueKey(value: WorkspaceToolParamScalarValue) {
  return `${typeof value}:${String(value)}`;
}

function hasTransparentPixels(value: unknown) {
  return (
    !!value &&
    typeof value === 'object' &&
    !Array.isArray(value) &&
    (value as { hasTransparentPixels?: unknown }).hasTransparentPixels === true
  );
}

function readScalarArrayValue(value: unknown, fallback: unknown) {
  const source = Array.isArray(value)
    ? value
    : Array.isArray(fallback)
      ? fallback
      : [];
  return source.filter(isWorkspaceParamScalar);
}

function toggleScalarArrayValue(params: {
  values: WorkspaceToolParamScalarValue[];
  value: WorkspaceToolParamScalarValue;
  checked: boolean;
}) {
  if (params.checked) {
    return params.values.some((item) => Object.is(item, params.value))
      ? params.values
      : [...params.values, params.value];
  }
  return params.values.filter((item) => !Object.is(item, params.value));
}

function getSliderPrecision(step: number) {
  const text = String(step);
  const decimalIndex = text.indexOf('.');
  return decimalIndex < 0 ? 0 : text.length - decimalIndex - 1;
}

function normalizeSliderValue(params: {
  value: number;
  min: number;
  max: number;
  step: number;
}) {
  const clamped = Math.min(params.max, Math.max(params.min, params.value));
  const steps = Math.round((clamped - params.min) / params.step);
  const normalized = params.min + steps * params.step;
  return Number(normalized.toFixed(getSliderPrecision(params.step)));
}

function readSliderValue(
  value: unknown,
  fallback: unknown,
  min: number,
  max: number,
  step: number
) {
  const numericValue =
    typeof value === 'number'
      ? value
      : typeof fallback === 'number'
        ? fallback
        : min;
  return normalizeSliderValue({ value: numericValue, min, max, step });
}

function WorkspaceSliderInlineEditor({
  label,
  value,
  min,
  max,
  step,
  editing,
  onEditingChange,
  onChange,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  step: number;
  editing: boolean;
  onEditingChange: (editing: boolean) => void;
  onChange: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(value));
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!editing) {
      setDraft(String(value));
      return;
    }
    setDraft(String(value));
    inputRef.current?.focus();
    inputRef.current?.select();
  }, [editing, value]);

  const commit = () => {
    const parsed = Number(draft);
    if (Number.isFinite(parsed)) {
      onChange(normalizeSliderValue({ value: parsed, min, max, step }));
    } else {
      setDraft(String(value));
    }
    onEditingChange(false);
  };

  if (!editing) return null;

  return (
    <input
      ref={inputRef}
      aria-label={`${label} value`}
      className="cms-block-workspace-workspace-1-workspace-form-controls__paramSliderThumbInput"
      min={min}
      max={max}
      step={step}
      type="number"
      value={draft}
      onBlur={commit}
      onChange={(event) => setDraft(event.target.value)}
      onPointerDown={(event) => event.stopPropagation()}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          event.currentTarget.blur();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(String(value));
          onEditingChange(false);
        }
      }}
    />
  );
}

function WorkspaceContinuousSlider({
  param,
  value,
  onChange,
}: {
  param: WorkspaceToolParamView;
  value: number;
  onChange: (value: number) => void;
}) {
  const min = param.min ?? 0;
  const max = param.max ?? 100;
  const step = param.step ?? 1;
  const range = Math.max(step, max - min);
  const progress = ((value - min) / range) * 100;
  const snapPoints = (param.sliderSnapPoints ?? []).filter(
    (point) => point >= min && point <= max
  );
  const [dragging, setDragging] = useState(false);
  const [editing, setEditing] = useState(false);
  const pointerStart = useRef<{
    clientX: number;
    startedOnThumb: boolean;
    dragged: boolean;
  } | null>(null);
  const thumbPosition = getWorkspaceContinuousSliderThumbInset({
    value,
    min,
    max,
  });

  const pointerStartedOnThumb = (input: HTMLInputElement, clientX: number) => {
    const rect = input.getBoundingClientRect();
    const center = getWorkspaceContinuousSliderThumbCenterPx({
      value,
      min,
      max,
      trackLeft: rect.left,
      trackRight: rect.right,
      rtl: getComputedStyle(input).direction === 'rtl',
    });
    return (
      Math.abs(clientX - center) <=
      WORKSPACE_CONTINUOUS_SLIDER_THUMB_SIZE_PX / 2 + 3
    );
  };

  const finishPointerInteraction = (clientX: number) => {
    const start = pointerStart.current;
    if (!start) return;
    const moved = Math.abs(clientX - start.clientX) > 4 || start.dragged;
    setDragging(false);
    pointerStart.current = null;
    if (start.startedOnThumb && !moved) {
      setEditing(true);
    }
  };

  return (
    <div
      className="cms-block-workspace-workspace-1-workspace-form-controls__paramContinuousSlider"
      data-dragging={dragging ? 'true' : undefined}
      data-editing={editing ? 'true' : undefined}
    >
      <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramContinuousSliderTrack">
        <input
          aria-label={param.label}
          aria-valuemax={max}
          aria-valuemin={min}
          aria-valuenow={value}
          max={max}
          min={min}
          step={step}
          type="range"
          value={value}
          style={
            {
              '--workspace-slider-progress': `${progress}%`,
            } as CSSProperties
          }
          onChange={(event) => {
            onChange(
              normalizeSliderValue({
                value: Number(event.target.value),
                min,
                max,
                step,
              })
            );
          }}
          onPointerDown={(event) => {
            if (editing) return;
            pointerStart.current = {
              clientX: event.clientX,
              startedOnThumb: pointerStartedOnThumb(
                event.currentTarget,
                event.clientX
              ),
              dragged: false,
            };
          }}
          onPointerMove={(event) => {
            const start = pointerStart.current;
            if (!start?.startedOnThumb) return;
            if (Math.abs(event.clientX - start.clientX) <= 4) return;
            start.dragged = true;
            setDragging(true);
          }}
          onPointerUp={(event) => finishPointerInteraction(event.clientX)}
          onPointerCancel={() => {
            pointerStart.current = null;
            setDragging(false);
          }}
        />
        <div
          aria-hidden="true"
          className="cms-block-workspace-workspace-1-workspace-form-controls__paramContinuousSliderTicks"
        >
          {snapPoints.map((point) => (
            <span
              key={point}
              style={{
                insetInlineStart: `${((point - min) / range) * 100}%`,
              }}
            />
          ))}
        </div>
        {!dragging && !editing ? (
          <span
            aria-hidden="true"
            className="cms-block-workspace-workspace-1-workspace-form-controls__paramSliderThumbValue"
            style={{ insetInlineStart: thumbPosition }}
          >
            {value}
          </span>
        ) : null}
        {dragging ? (
          <span
            aria-live="polite"
            className="cms-block-workspace-workspace-1-workspace-form-controls__paramSliderDragTooltip"
            style={{ insetInlineStart: thumbPosition }}
          >
            {value}
          </span>
        ) : null}
        <div
          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSliderThumbEditor"
          style={{ insetInlineStart: thumbPosition }}
        >
          <WorkspaceSliderInlineEditor
            label={param.label}
            value={value}
            min={min}
            max={max}
            step={step}
            editing={editing}
            onEditingChange={setEditing}
            onChange={onChange}
          />
        </div>
      </div>
    </div>
  );
}

export function WorkspaceParamControls({
  fields,
  advancedLabel,
  tool,
  values,
  calculatorContext,
  calculatorCopy,
  toolId,
  templatePicker,
  templatePickerLabels,
  assetsOpen,
  repeatGeneration,
  modePreviousLabel,
  modeNextLabel,
  premiumFeatureTooltip,
  transparentImageRestrictionTooltip,
  showPaidFeatureMarkers,
  onPremiumFeatureClick,
  onCalculatorApply,
  onTemplateAccessRequest,
  onTemplateCustomImageUpload,
  onOpenAssets,
  onChange,
  onSizeValidityChange,
  resultImageUrl,
  resultImageDimensions,
  imageInfoContainer,
}: {
  fields: WorkspaceStudioViewModel['activeTool']['fields'];
  advancedLabel?: string;
  tool?: WorkspaceStudioViewModel['activeTool'];
  values: WorkspaceStudioViewModel['activeToolParams'];
  calculatorContext?: WorkspaceStudioViewModel['parameterCalculatorContext'];
  calculatorCopy?: WorkspaceStudioViewModel['shell']['controls']['calculatorCopy'];
  toolId?: string;
  templatePicker?: WorkspaceToolTemplatePickerView;
  templatePickerLabels?: Pick<
    WorkspaceStudioViewModel['shell']['controls'],
    | 'assetsLabel'
    | 'clearHistorySelectionLabel'
    | 'premiumFeatureTooltip'
    | 'toolCatalogNextPageLabel'
    | 'toolCatalogPreviousPageLabel'
    | 'toolPickerCloseLabel'
    | 'uploadRemoveImageLabel'
    | 'uploadTileLabel'
  > &
    NonNullable<
      WorkspaceStudioViewModel['shell']['controls']['templatePickerCopy']
    >;
  assetsOpen?: boolean;
  repeatGeneration?: {
    label: string;
    maxJobs: number;
    value: number;
    onChange: (count: number) => void;
  } | null;
  modePreviousLabel?: string;
  modeNextLabel?: string;
  premiumFeatureTooltip: string;
  transparentImageRestrictionTooltip?: string;
  showPaidFeatureMarkers: boolean;
  onPremiumFeatureClick?: (param: WorkspaceToolParamView) => void;
  onCalculatorApply?: WorkspaceStudioViewActions['handleParameterCalculatorApply'];
  onTemplateAccessRequest?: (
    accessPolicy: WorkspaceToolAccessPolicyRule
  ) => boolean;
  onTemplateCustomImageUpload?: (
    groupId: string,
    file: File
  ) => Promise<boolean>;
  onOpenAssets?: (target: WorkspaceAssetTarget) => void;
  onChange: WorkspaceStudioViewActions['handleToolParamChange'];
  onSizeValidityChange?: (valid: boolean) => void;
  resultImageUrl?: string | null;
  resultImageDimensions?: WorkspaceStudioViewModel['canvasResultImageDimensions'];
  imageInfoContainer?: HTMLElement | null;
}) {
  const mobileDrawerOverlayHost = useWorkspaceMobileDrawerOverlayHost();
  const isMobileViewport = useWorkspaceIsMobileViewport();
  const calculator = tool?.parameterCalculator;
  const primaryImageFieldId =
    tool?.inputBindings?.primaryImageFieldId ?? 'image';
  const transparentUpscalerInput =
    toolId === 'img-fast-upscale' &&
    hasTransparentPixels(
      calculatorContext?.clientImageMeta[primaryImageFieldId]
    );
  useEffect(() => {
    if (!transparentUpscalerInput) return;
    if (values.scene !== 'general') onChange('scene', 'general');
    if (values.highQuality !== false) onChange('highQuality', false);
    if (values.detailLevel !== false) onChange('detailLevel', false);
  }, [
    onChange,
    transparentUpscalerInput,
    values.detailLevel,
    values.highQuality,
    values.scene,
  ]);
  if (
    calculator &&
    (!calculatorContext || !calculatorCopy || !onCalculatorApply)
  ) {
    throw new Error(
      `Workspace parameter calculator runtime is incomplete for Tool ${tool?.toolId ?? 'unknown'}.`
    );
  }
  if (transparentUpscalerInput && !transparentImageRestrictionTooltip?.trim()) {
    throw new Error(
      'Transparent Upscaler restrictions require localized tooltip copy.'
    );
  }
  if (fields.length === 0 && !repeatGeneration && !templatePicker) {
    return null;
  }
  const templateSelectionFieldIds = new Set(
    templatePicker?.groups.map((group) => group.selectionFieldId) ?? []
  );
  if (
    templatePicker &&
    (!toolId ||
      !templatePickerLabels ||
      !onTemplateAccessRequest ||
      !onTemplateCustomImageUpload ||
      !onOpenAssets)
  ) {
    throw new Error(
      'Workspace template picker requires its Tool, copy, access, upload and history owners.'
    );
  }
  const effectiveValues = {
    ...buildDefaultWorkspaceToolParams(fields),
    ...values,
  };
  const renderableFields = fields
    .filter(
      (field) =>
        isRenderableParamType(field) &&
        !(
          field.id === UPSCALER_CUSTOM_SIZE_FIELD &&
          fields.some(
            (candidate) => candidate.controlVariant === 'upscale-size'
          )
        ) &&
        !templateSelectionFieldIds.has(field.id) &&
        isAdminV2ToolFieldActive(field, effectiveValues)
    )
    .map((field) =>
      presentWorkspaceParameter({
        ...field,
        options: field.options?.map((option) =>
          option.labelOverride &&
          Object.is(
            effectiveValues[option.labelOverride.fieldId],
            option.labelOverride.value
          )
            ? { ...option, label: option.labelOverride.label }
            : option
        ),
      })
    );
  const renderHelp = (item: {
    label: string;
    infoText?: string;
    helpPreview?: WorkspaceToolParamView['helpPreview'];
  }) => {
    if (!item.helpPreview || !item.infoText) return null;
    if (!templatePickerLabels)
      throw new Error(
        'Workspace help preview requires localized preview copy.'
      );
    return (
      <WorkspaceHelpPopover
        label={item.label}
        text={item.infoText}
        preview={item.helpPreview}
        loadErrorLabel={templatePickerLabels.templatePreviewLoadError}
      />
    );
  };

  const advancedFields = renderableFields.filter(
    (param) => param.group === 'advanced'
  );
  if (advancedFields.length > 0 && !advancedLabel?.trim()) {
    throw new Error('Workspace advanced parameters require localized copy.');
  }
  const pairAdjacentSelects =
    tool?.viewKind === 'reference-composer' &&
    tool.outputBindings.mediaKind === 'video';
  const mainGroups = groupWorkspaceParameters({
    fields: renderableFields.filter((field) => field.group !== 'advanced'),
    pairAdjacentSelects,
  });
  const advancedGroups = groupWorkspaceParameters({
    fields: advancedFields,
    pairAdjacentSelects,
  });
  const renderParam = (param: WorkspaceToolParamView) => {
    if (param.controlVariant === 'upscale-size') {
      if (!tool || !onCalculatorApply)
        throw new Error(
          'Upscale size requires Tool and parameter apply owners.'
        );
      return (
        <WorkspaceUpscaleSize
          imageInfoContainer={imageInfoContainer}
          key={`${tool.toolId}:${param.id}`}
          param={param}
          values={effectiveValues}
          imageMeta={
            calculatorContext?.clientImageMeta[
              tool.inputBindings?.primaryImageFieldId ?? 'image'
            ]
          }
          inputImageUrl={calculatorContext?.inputImageUrl}
          resultImageUrl={resultImageUrl}
          resultImageDimensions={resultImageDimensions}
          onApply={onCalculatorApply}
          onValidityChange={onSizeValidityChange}
          showPaidFeatureMarkers={showPaidFeatureMarkers}
          premiumFeatureTooltip={premiumFeatureTooltip}
        />
      );
    }
    const infoText =
      param.options?.find(
        (option) =>
          !option.helpPreview && Object.is(option.value, values[param.id])
      )?.infoText ?? param.infoText;
    const currentSwitchValue = values[param.id] === true;
    const currentArrayValue = readScalarArrayValue(
      values[param.id],
      param.defaultValue
    );
    const selectedOption =
      param.type === 'select'
        ? param.options?.find((option) =>
            Object.is(option.value, values[param.id] ?? param.defaultValue)
          )
        : undefined;
    const steppedSlider =
      param.type === 'select' && param.controlVariant === 'stepped-slider';
    const continuousSlider = param.type === 'slider';
    const modeTabs = param.type === 'select' && param.controlVariant === 'tabs';
    const sliderMin = param.min ?? 0;
    const sliderMax = param.max ?? 100;
    const sliderStep = param.step ?? 1;
    const sliderValue = continuousSlider
      ? readSliderValue(
          values[param.id],
          param.defaultValue,
          sliderMin,
          sliderMax,
          sliderStep
        )
      : 0;
    const transparentRestriction =
      transparentUpscalerInput &&
      (param.id === 'highQuality' || param.id === 'detailLevel');
    const selectedIndex =
      param.options?.findIndex((option) =>
        Object.is(option.value, values[param.id] ?? param.defaultValue)
      ) ?? -1;
    return (
      <div
        className="cms-block-workspace-workspace-1-workspace-form-controls__paramRow"
        key={param.id}
      >
        <div
          className={cn(
            'cms-block-workspace-workspace-1-workspace-form-controls__paramRowMain',
            param.type === 'select' &&
              !steppedSlider &&
              !modeTabs &&
              'cms-block-workspace-workspace-1-workspace-form-controls__paramRowMainSelect',
            param.type === 'switch' &&
              'cms-block-workspace-workspace-1-workspace-form-controls__paramRowMainSwitch',
            (param.type === 'segmented-slider' || steppedSlider || modeTabs) &&
              'cms-block-workspace-workspace-1-workspace-form-controls__paramRowMainSegmentedSlider',
            continuousSlider &&
              'cms-block-workspace-workspace-1-workspace-form-controls__paramRowMainContinuousSlider'
          )}
        >
          <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramLabelWrap">
            <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramLabel">
              {param.label}
            </span>
            {param.helpPreview ? (
              renderHelp(param)
            ) : infoText ? (
              <WorkspaceInfoTooltip text={infoText} />
            ) : null}
            {continuousSlider ? (
              <span
                aria-hidden="true"
                className="cms-block-workspace-workspace-1-workspace-form-controls__paramContinuousSliderRange"
              >
                {sliderMin}–{sliderMax}
              </span>
            ) : null}
            {steppedSlider ? (
              <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramSteppedSliderValue">
                {selectedOption?.label}
                {showPaidFeatureMarkers && selectedOption?.requiredPaid ? (
                  <PremiumCrownIcon tooltipText={premiumFeatureTooltip} />
                ) : null}
              </span>
            ) : null}
          </span>
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramControlWrap">
            {modeTabs ? (
              <WorkspaceModeTabs
                label={param.label}
                items={(param.options ?? []).map((option) => {
                  const transparentRestriction =
                    transparentUpscalerInput &&
                    param.id === 'scene' &&
                    option.value !== 'general';
                  return {
                    id: getOptionValueKey(option.value),
                    label: option.label,
                    disabled: option.disabled || transparentRestriction,
                    disabledReason: transparentRestriction
                      ? transparentImageRestrictionTooltip
                      : undefined,
                    help: renderHelp(option),
                    thumbnail: option.helpPreview?.posterUrl,
                  };
                })}
                selectedId={
                  selectedOption
                    ? getOptionValueKey(selectedOption.value)
                    : undefined
                }
                previousLabel={modePreviousLabel}
                nextLabel={modeNextLabel}
                onSelect={(id) => {
                  const option = param.options?.find(
                    (candidate) => getOptionValueKey(candidate.value) === id
                  );
                  if (option) onChange(param.id, option.value);
                }}
              />
            ) : steppedSlider ? (
              <div
                className="cms-block-workspace-workspace-1-workspace-form-controls__paramSteppedSlider"
                data-disabled={transparentRestriction ? 'true' : undefined}
              >
                <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramSteppedSliderTrack">
                  <input
                    type="range"
                    aria-label={param.label}
                    aria-valuetext={selectedOption?.label}
                    min={0}
                    max={(param.options?.length ?? 1) - 1}
                    step={1}
                    value={Math.max(0, selectedIndex)}
                    disabled={!selectedOption || transparentRestriction}
                    style={
                      {
                        '--workspace-slider-progress': `${(selectedIndex / Math.max(1, (param.options?.length ?? 1) - 1)) * 100}%`,
                      } as CSSProperties
                    }
                    onChange={(event) => {
                      const option =
                        param.options?.[Number(event.target.value)];
                      if (option && !option.disabled) {
                        onChange(param.id, option.value);
                      }
                    }}
                  />
                  <div
                    className="cms-block-workspace-workspace-1-workspace-form-controls__paramSteppedSliderTicks"
                    aria-hidden="true"
                  >
                    {param.options?.map((option, index) => (
                      <span
                        key={getOptionValueKey(option.value)}
                        style={
                          index === selectedIndex
                            ? { visibility: 'hidden' }
                            : undefined
                        }
                      />
                    ))}
                  </div>
                </div>
                <div
                  className="cms-block-workspace-workspace-1-workspace-form-controls__paramSteppedSliderLabels"
                  aria-hidden="true"
                >
                  <span>{param.options?.[0]?.label}</span>
                  <span>{param.options?.at(-1)?.label}</span>
                </div>
              </div>
            ) : param.type === 'segmented-slider' ? (
              <div
                className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSlider"
                data-disabled={transparentRestriction ? 'true' : undefined}
                data-sliding={selectedIndex >= 0 ? 'true' : undefined}
                style={
                  {
                    '--workspace-segment-count': param.options?.length,
                    '--workspace-segment-index': selectedIndex,
                  } as CSSProperties
                }
                role="radiogroup"
                aria-label={param.label}
              >
                {param.options?.map((option) => {
                  const selected = Object.is(
                    option.value,
                    values[param.id] ?? param.defaultValue
                  );
                  return (
                    <div
                      key={getOptionValueKey(option.value)}
                      className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderOption"
                      data-selected={selected ? 'true' : 'false'}
                    >
                      <label className="flex min-w-0 flex-1 cursor-pointer items-center justify-center">
                        <input
                          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderInput"
                          type="radio"
                          name={param.id}
                          value={getOptionValueKey(option.value)}
                          checked={selected}
                          disabled={transparentRestriction || option.disabled}
                          onChange={() => {
                            if (!transparentRestriction && !option.disabled) {
                              onChange(param.id, option.value);
                            }
                          }}
                        />
                        <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderOptionLabel">
                          {option.label}
                        </span>
                      </label>
                      {renderHelp(option)}
                      {showPaidFeatureMarkers && option.requiredPaid ? (
                        <PremiumCrownIcon
                          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderPremiumIcon"
                          iconClassName="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderPremiumIconGlyph"
                          tooltipText={premiumFeatureTooltip}
                        />
                      ) : null}
                    </div>
                  );
                })}
              </div>
            ) : param.type === 'select' ? (
              <Select
                value={
                  selectedOption ? getOptionValueKey(selectedOption.value) : ''
                }
                onValueChange={(value) => {
                  const option = param.options?.find(
                    (candidate) => getOptionValueKey(candidate.value) === value
                  );
                  if (option) {
                    onChange(param.id, option.value);
                  }
                }}
              >
                <SelectTrigger className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectTrigger">
                  <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectValueWrap">
                    <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectValueText">
                      {selectedOption?.label ??
                        String(values[param.id] ?? param.defaultValue)}
                    </span>
                    {showPaidFeatureMarkers && selectedOption?.requiredPaid ? (
                      <PremiumCrownIcon
                        className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectTriggerPremiumIcon"
                        iconClassName="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectTriggerPremiumIconGlyph"
                        tooltipText={premiumFeatureTooltip}
                      />
                    ) : null}
                  </span>
                </SelectTrigger>
                <SelectContent
                  className={cn(
                    'cms-block-workspace-workspace-1-workspace-form-controls__paramSelectContent',
                    (param.options?.length ?? 0) > 8 && 'max-h-64'
                  )}
                  portal={mobileDrawerOverlayHost !== null}
                  portalContainer={mobileDrawerOverlayHost}
                >
                  {param.options?.map((option) => (
                    <SelectItem
                      key={getOptionValueKey(option.value)}
                      value={getOptionValueKey(option.value)}
                      className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectItem"
                      tooltipText={
                        showPaidFeatureMarkers && option.requiredPaid
                          ? premiumFeatureTooltip
                          : undefined
                      }
                      tooltipBodyClassName="max-w-72 text-sm leading-6 sm:max-w-80"
                      tooltipContentClassName="px-3 py-2.5"
                      trailingContent={
                        showPaidFeatureMarkers && option.requiredPaid ? (
                          <PremiumCrownIcon
                            className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectPremiumIcon"
                            iconClassName="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectPremiumIconGlyph"
                            showTooltip={false}
                          />
                        ) : undefined
                      }
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : param.type === 'multi-select' ? (
              <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramOptionList">
                {param.options?.map((option) => {
                  const checked = currentArrayValue.some((value) =>
                    Object.is(value, option.value)
                  );
                  return (
                    <label
                      className="cms-block-workspace-workspace-1-workspace-form-controls__paramOption"
                      key={getOptionValueKey(option.value)}
                    >
                      <input
                        checked={checked}
                        type="checkbox"
                        onChange={(event) => {
                          onChange(
                            param.id,
                            toggleScalarArrayValue({
                              values: currentArrayValue,
                              value: option.value,
                              checked: event.target.checked,
                            })
                          );
                        }}
                      />
                      <span>{option.label}</span>
                      {showPaidFeatureMarkers && option.requiredPaid ? (
                        <PremiumCrownIcon
                          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectPremiumIcon"
                          iconClassName="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectPremiumIconGlyph"
                          showTooltip={false}
                        />
                      ) : null}
                    </label>
                  );
                })}
              </div>
            ) : param.type === 'text' || param.type === 'url' ? (
              <input
                className={cn(
                  'cms-block-workspace-workspace-1-workspace-form-controls__inputControl',
                  'cms-block-workspace-workspace-1-workspace-form-controls__paramTextInput'
                )}
                type={param.type === 'url' ? 'url' : 'text'}
                aria-label={param.label}
                placeholder={param.placeholder}
                value={String(values[param.id] ?? param.defaultValue ?? '')}
                onChange={(event) => {
                  onChange(param.id, event.target.value);
                }}
              />
            ) : param.type === 'array' ? (
              <input
                className={cn(
                  'cms-block-workspace-workspace-1-workspace-form-controls__inputControl',
                  'cms-block-workspace-workspace-1-workspace-form-controls__paramTextInput'
                )}
                type="text"
                aria-label={param.label}
                placeholder={param.placeholder}
                value={currentArrayValue.map(String).join(', ')}
                onChange={(event) => {
                  onChange(
                    param.id,
                    event.target.value
                      .split(',')
                      .map((item) => item.trim())
                      .filter(Boolean)
                  );
                }}
              />
            ) : param.type === 'switch' ? (
              <>
                {showPaidFeatureMarkers &&
                param.requiredPaid &&
                !transparentRestriction ? (
                  <PremiumCrownIcon
                    className={cn(
                      'cms-block-workspace-workspace-1-workspace-form-controls__paramHintTrigger',
                      'cms-block-workspace-workspace-1-workspace-form-controls__paramHintTriggerControl',
                      'cms-block-workspace-workspace-1-workspace-form-controls__paramHintTriggerPremium',
                      'cms-block-workspace-workspace-1-workspace-form-controls__paramControlPremiumIcon'
                    )}
                    iconClassName="cms-block-workspace-workspace-1-workspace-form-controls__paramControlPremiumIconGlyph"
                    onClick={
                      onPremiumFeatureClick
                        ? () => onPremiumFeatureClick(param)
                        : undefined
                    }
                    opticalOffsetY="0"
                    showNativeTitle={
                      !isMobileViewport || !onPremiumFeatureClick
                    }
                    showTooltip={!isMobileViewport || !onPremiumFeatureClick}
                    tooltipText={premiumFeatureTooltip}
                  />
                ) : null}
                {transparentRestriction ? (
                  <WorkspaceInfoTooltip
                    className="cms-block-workspace-workspace-1-workspace-form-controls__paramRestrictionTrigger"
                    icon={renderAppIcon({
                      name: 'forbid-circle',
                      className:
                        'cms-block-workspace-workspace-1-workspace-form-controls__paramRestrictionIcon',
                    })}
                    text={transparentImageRestrictionTooltip!}
                  />
                ) : null}
                <AccessControlledSwitch
                  checked={currentSwitchValue}
                  aria-label={param.label}
                  className="cms-block-workspace-workspace-1-workspace-form-controls__paramSwitch"
                  disabled={transparentRestriction}
                  onCheckedChange={(checked) => {
                    onChange(param.id, checked);
                  }}
                />
              </>
            ) : param.type === 'slider' ? (
              <WorkspaceContinuousSlider
                param={param}
                value={sliderValue}
                onChange={(next) => onChange(param.id, next)}
              />
            ) : param.type === 'number' ? (
              <input
                className={cn(
                  'cms-block-workspace-workspace-1-workspace-form-controls__inputControl',
                  'cms-block-workspace-workspace-1-workspace-form-controls__paramNumberInput'
                )}
                type="number"
                aria-label={param.label}
                min={param.min}
                max={param.max}
                step={param.step}
                placeholder={param.placeholder}
                value={readNumberInputValue(
                  values[param.id] ?? param.defaultValue
                )}
                onChange={(event) => {
                  onChange(
                    param.id,
                    event.target.value === '' ? '' : Number(event.target.value)
                  );
                }}
              />
            ) : null}
          </div>
        </div>
        {calculator &&
        calculator.anchorFieldId === param.id &&
        calculatorContext &&
        calculatorCopy &&
        onCalculatorApply ? (
          <WorkspaceParameterCalculator
            key={`${tool.toolId}:${calculator.id}:${calculator.moduleId}`}
            calculator={calculator}
            context={calculatorContext}
            copy={calculatorCopy}
            tool={tool}
            values={values}
            onApply={onCalculatorApply}
          />
        ) : null}
      </div>
    );
  };
  const renderParamGroup = (
    group: ReturnType<typeof groupWorkspaceParameters>[number]
  ) => {
    if (group.kind === 'paired-selects') {
      return (
        <div
          key={group.fields[0]!.id}
          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSelectPair"
        >
          {group.fields.map(renderParam)}
        </div>
      );
    }
    if (group.kind === 'switches' && group.fields.length > 1) {
      return (
        <div
          key={group.fields[0]!.id}
          className="cms-block-workspace-workspace-1-workspace-form-controls__paramSwitchGroup"
        >
          {group.fields.map(renderParam)}
        </div>
      );
    }
    return renderParam(group.fields[0]!);
  };
  return (
    <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramList">
      {templatePicker &&
      toolId &&
      templatePickerLabels &&
      onTemplateAccessRequest &&
      onTemplateCustomImageUpload &&
      onOpenAssets ? (
        <WorkspaceTemplatePicker
          key={toolId}
          toolId={toolId}
          picker={templatePicker}
          values={values}
          labels={templatePickerLabels}
          assetsOpen={assetsOpen === true}
          showPaidFeatureMarkers={showPaidFeatureMarkers}
          onChange={onChange}
          onOpenAssets={onOpenAssets}
          onRequestAccess={onTemplateAccessRequest}
          onUploadCustomImage={onTemplateCustomImageUpload}
        />
      ) : null}
      {repeatGeneration ? (
        <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramRow">
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramRowMain cms-block-workspace-workspace-1-workspace-form-controls__paramRowMainSegmentedSlider">
            <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramLabelWrap">
              <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramLabel">
                {repeatGeneration.label}
              </span>
            </span>
            <div
              aria-label={repeatGeneration.label}
              className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSlider"
              data-sliding="true"
              style={
                {
                  '--workspace-segment-count': repeatGeneration.maxJobs,
                  '--workspace-segment-index': repeatGeneration.value - 1,
                } as CSSProperties
              }
              role="radiogroup"
            >
              {Array.from(
                { length: repeatGeneration.maxJobs },
                (_, index) => index + 1
              ).map((count) => {
                const selected = count === repeatGeneration.value;
                return (
                  <label
                    className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderOption"
                    data-selected={selected ? 'true' : 'false'}
                    key={count}
                  >
                    <input
                      checked={selected}
                      className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderInput"
                      name="workspace-repeat-generation-count"
                      type="radio"
                      value={count}
                      onChange={() => repeatGeneration.onChange(count)}
                    />
                    <span className="cms-block-workspace-workspace-1-workspace-form-controls__paramSegmentedSliderOptionLabel">
                      {count}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
      ) : null}
      {mainGroups.map(renderParamGroup)}
      {advancedFields.length > 0 ? (
        <details
          key={toolId}
          className="cms-block-workspace-workspace-1-workspace-form-controls__advanced"
        >
          <summary className="cms-block-workspace-workspace-1-workspace-form-controls__advancedSummary">
            <span>{advancedLabel}</span>
            {renderAppIcon({
              name: 'chevron-down',
              className:
                'cms-block-workspace-workspace-1-workspace-form-controls__advancedChevron',
            })}
          </summary>
          <div className="cms-block-workspace-workspace-1-workspace-form-controls__paramList">
            {advancedGroups.map(renderParamGroup)}
          </div>
        </details>
      ) : null}
    </div>
  );
}
